import { useQuery, type QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    InvoiceDetailResponse,
    InvoiceOriginalExtracted,
    InvoicePart,
} from '../../types/invoice';

// Re-export commonly used invoice detail types so existing imports continue to work.
export type {
    InvoicePart,
    InvoiceOriginalExtracted,
    InvoiceDetailResponse,
};

interface InvoiceDetailInput {
    invoiceId: string;
}

const getInvoiceDetail = async (sessionId: string, invoiceId: string): Promise<InvoiceDetailResponse> => {
    const { data } = await axios.post(`${API_URL}/invoice/detail`,
        {
            invoiceId
        } satisfies InvoiceDetailInput,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export function invoiceDetailQueryKey(invoiceId: string) {
    return ['/invoice/detail', invoiceId] as const;
}

export function prefetchInvoiceDetail(
    queryClient: QueryClient,
    sessionId: string,
    invoiceId: string,
) {
    if (!sessionId || !invoiceId) return Promise.resolve();
    return queryClient.prefetchQuery({
        queryKey: invoiceDetailQueryKey(invoiceId),
        queryFn: () => getInvoiceDetail(sessionId, invoiceId),
    });
}

export const useGetInvoiceDetail = (invoiceId: string) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: invoiceDetailQueryKey(invoiceId),
        queryFn: () => getInvoiceDetail(sessionContext?.sessionId ?? '', invoiceId),
        enabled: !!invoiceId && !!sessionContext?.sessionId,
    });
};

